/**
 * Auth Store — Zustand store for authentication state (Vite edition)
 *
 * Features:
 * - Ghost Login prevention (expires_at check)
 * - Three-level session recovery
 * - Explicit logout flag (SIGNED_OUT prevention)
 * - Structured logging via logger-client.ts engine (#174)
 * - Auth validation & session management via auth-client.ts engine (#186)
 *
 * Key difference from Next.js version:
 * - Uses @supabase/supabase-js directly (localStorage session)
 * - No cookie-based SSR
 * - import.meta.env instead of process.env
 *
 * @see Issue #88
 * @see Issue #174
 * @see Issue #186
 */
import { create } from "zustand";
import type { Session, User, AuthChangeEvent } from "@supabase/supabase-js";
import { getSupabase } from "@/lib/supabase";
import { markExplicitLogout, isExplicitLogout, clearExplicitLogout } from "@/lib/logout-flag";
import { trackEvent, identifyUser, resetIdentity, PostHogEvents } from "@/lib/posthog";
import { createLogger } from '@/lib/logger';
// Auth-client engine (#186) — display info derivation + session expiry check
import { getAuthDisplayInfo as engineDisplayInfo, type AuthStore as EngineAuthStore } from "@shared-lib/auth-client";

const log = createLogger('AuthStore');

// #258/#260/#502: Track auth subscription to prevent duplicate listeners.
// Module-level is intentional here — Zustand stores are singletons and this
// ensures only one onAuthStateChange listener exists across HMR reloads.
let _authSubscription: { unsubscribe: () => void } | null = null;

export type AuthState = "loading" | "authenticated" | "unauthenticated";

export interface AuthProfile {
  id: string;
  email: string;
  display_name: string;
  avatar_url?: string;
}

interface AuthStoreState {
  state: AuthState;
  user: AuthProfile | null;
  session: Session | null;
  initialize: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<{ error: string | null }>;
  signUpWithEmail: (email: string, password: string, displayName: string) => Promise<{ error: string | null }>;
  signInWithOAuth: (provider: "google" | "github") => Promise<void>;
  signOut: () => Promise<void>;
  sendPasswordReset: (email: string) => Promise<{ error: string | null }>;
  updatePassword: (password: string) => Promise<{ error: string | null }>;
  _setFromSession: (session: Session | null) => void;
}

function profileFromUser(u: User): AuthProfile {
  return {
    id: u.id,
    email: u.email ?? "",
    display_name:
      u.user_metadata?.display_name ??
      u.user_metadata?.full_name ??
      u.email?.split("@")[0] ??
      "User",
    avatar_url: u.user_metadata?.avatar_url,
  };
}

export const useAuthStore = create<AuthStoreState>((set, get) => ({
  state: "loading",
  user: null,
  session: null,

  initialize: async () => {
    try {
      const supabase = getSupabase();
      const { data: { session } } = await supabase.auth.getSession();

      if (session) {
        const nowSec = Math.floor(Date.now() / 1000);
        const isExpiring = session.expires_at != null && session.expires_at <= nowSec + 60;
        if (isExpiring) {
          log.info('session expiring, refreshing');
          const { data: refreshed } = await supabase.auth.refreshSession();
          if (refreshed.session) {
            get()._setFromSession(refreshed.session);
          } else {
            log.warn('session refresh failed');
            set({ state: "unauthenticated", user: null, session: null });
          }
        } else {
          get()._setFromSession(session);
        }
      } else {
        set({ state: "unauthenticated", user: null, session: null });
      }

      // Listen for auth state changes (#258/#260: track subscription to prevent duplicates)
      if (_authSubscription) _authSubscription.unsubscribe();
      const { data: { subscription } } = supabase.auth.onAuthStateChange((event: AuthChangeEvent, newSession: Session | null) => {
        if (event === "SIGNED_OUT") {
          if (!isExplicitLogout()) return; // Ignore ghost SIGNED_OUT
          clearExplicitLogout();
          set({ state: "unauthenticated", user: null, session: null });
        } else if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
          clearExplicitLogout();
          get()._setFromSession(newSession);
        }
      });
      _authSubscription = subscription;
    } catch (err) {
      log.error('auth initialization failed', { error: String(err) });
      set({ state: "unauthenticated", user: null, session: null });
    }
  },

  signInWithEmail: async (email, password) => {
    const supabase = getSupabase();
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error: translateAuthError(error.message) };
    get()._setFromSession(data.session);
    trackEvent(PostHogEvents.USER_LOGGED_IN, { method: "email" });
    if (data.session?.user) identifyUser(data.session.user.id, { email });
    return { error: null };
  },

  signUpWithEmail: async (email, password, displayName) => {
    const supabase = getSupabase();
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { display_name: displayName },
        emailRedirectTo: window.location.origin + "/auth/callback",
      },
    });
    if (error) return { error: translateAuthError(error.message) };
    if (data.session) {
      get()._setFromSession(data.session);
      trackEvent(PostHogEvents.USER_REGISTERED, { method: "email" });
      if (data.session.user) identifyUser(data.session.user.id, { email, display_name: displayName });
    } else {
      // Email confirmation required
      set({ state: "unauthenticated", user: null, session: null });
    }
    return { error: null };
  },

  signInWithOAuth: async (provider) => {
    const supabase = getSupabase();
    await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: window.location.origin + "/auth/callback" },
    });
  },

  signOut: async () => {
    markExplicitLogout();
    const supabase = getSupabase();
    await supabase.auth.signOut();
    set({ state: "unauthenticated", user: null, session: null });
    trackEvent(PostHogEvents.USER_LOGGED_OUT);
    resetIdentity();
  },

  sendPasswordReset: async (email) => {
    const supabase = getSupabase();
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin + "/reset-password",
    });
    return { error: error?.message ?? null };
  },

  updatePassword: async (password) => {
    const supabase = getSupabase();
    const { error } = await supabase.auth.updateUser({ password });
    return { error: error?.message ?? null };
  },

  _setFromSession: (session) => {
    if (!session?.user) {
      set({ state: "unauthenticated", user: null, session: null });
      return;
    }
    set({
      state: "authenticated",
      user: profileFromUser(session.user),
      session,
    });
  },
}));

/**
 * Derive display info from current auth state using engine (#186).
 * Returns isLoading, isAuthenticated, userName, userEmail, avatarUrl.
 */
export function getDisplayInfo(): ReturnType<typeof engineDisplayInfo> {
  const { state, user } = useAuthStore.getState();
  const engineStore: EngineAuthStore = {
    state: state as 'loading' | 'authenticated' | 'unauthenticated',
    session: user ? {
      user: {
        id: user.id,
        email: user.email,
        display_name: user.display_name,
        avatar_url: user.avatar_url,
        provider: 'email',
        created_at: '',
      },
      access_token: '',
      refresh_token: '',
      expires_at: Date.now() + 3600_000,
    } : null,
    errors: [],
  };
  return engineDisplayInfo(engineStore);
}

// ── Auth error i18n (#293) ──────────────────────────────
const AUTH_ERROR_MAP: Record<string, string> = {
  "Invalid login credentials": "邮箱或密码错误",
  "Email not confirmed": "请先验证邮箱",
  "User already registered": "该邮箱已注册",
  "Password should be at least 6 characters": "密码至少需要 6 个字符",
  "Signup requires a valid password": "请输入有效密码",
  "Unable to validate email address: invalid format": "邮箱格式不正确",
  "Email rate limit exceeded": "操作过于频繁，请稍后再试",
  "For security purposes, you can only request this after": "操作过于频繁，请稍后再试",
};

function translateAuthError(msg: string): string {
  for (const [en, zh] of Object.entries(AUTH_ERROR_MAP)) {
    if (msg.includes(en)) return zh;
  }
  return msg;
}